import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Heart, MessageCircle } from "lucide-react";
import { apiAuth } from "../api";

const POLL_MS = 60000;

function timeAgo(iso) {
  if (!iso) return "";
  const sec = Math.max(1, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (sec < 60) return `${sec}s fa`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m fa`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h fa`;
  return `${Math.floor(h / 24)}g fa`;
}

const ICONS = {
  comment: MessageCircle,
  reaction: Heart,
};

export default function NotificationBell() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  const refresh = useCallback(async () => {
    try {
      const { data } = await apiAuth.get("/notifications?limit=30");
      setItems(data?.items || []);
      setUnread(data?.unread || 0);
    } catch {
      /* offline / 401 → silenzioso */
    }
  }, []);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, POLL_MS);
    const onVis = () => {
      if (document.visibilityState === "visible") refresh();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      clearInterval(t);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [refresh]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  async function openItem(n) {
    setOpen(false);
    try {
      await apiAuth.post("/notifications/read", { ids: [n.id] });
      setUnread((u) => Math.max(0, u - (n.read ? 0 : 1)));
      setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
    } catch {
      /* best effort */
    }
    if (n.post_id) navigate(`/post/${n.post_id}`);
    else navigate("/piazze");
  }

  async function markAll() {
    try {
      await apiAuth.post("/notifications/read", {});
      setUnread(0);
      setItems((prev) => prev.map((x) => ({ ...x, read: true })));
    } catch {
      /* ignore */
    }
  }

  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="relative inline-flex items-center justify-center w-10 h-10 rounded-full text-slate-300 hover:text-white transition-colors"
        aria-label={`Notifiche${unread ? ` (${unread} non lette)` : ""}`}
        title="Notifiche"
        data-testid="notification-bell"
      >
        <Bell size={20} aria-hidden />
        {unread > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#8b3a2b] text-white text-[10px] font-bold leading-[18px] text-center"
            data-testid="notification-badge"
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 top-12 w-80 max-w-[calc(100vw-2rem)] rounded-2xl border border-slate-200 bg-white shadow-xl z-50 overflow-hidden"
          role="menu"
          data-testid="notification-dropdown"
        >
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-100">
            <span className="text-sm font-semibold text-gray-950">Notifiche</span>
            {unread > 0 && (
              <button
                type="button"
                onClick={markAll}
                className="text-xs text-[#8b3a2b] hover:underline"
              >
                Segna tutte lette
              </button>
            )}
          </div>
          <div className="max-h-80 overflow-y-auto">
            {items.length === 0 ? (
              <p className="px-4 py-6 text-sm text-slate-500 text-center">
                Nessuna notifica. Quando qualcuno commenta o reagisce ai tuoi post le trovi qui.
              </p>
            ) : (
              items.map((n) => {
                const Icon = ICONS[n.type] || Bell;
                return (
                  <button
                    key={n.id}
                    type="button"
                    onClick={() => openItem(n)}
                    className={`w-full text-left flex items-start gap-3 px-4 py-3 hover:bg-slate-50 transition-colors ${
                      n.read ? "opacity-60" : ""
                    }`}
                  >
                    <span className="mt-0.5 shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-full bg-[#f4ede3] text-[#8b3a2b]">
                      <Icon size={14} aria-hidden />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm text-gray-950 font-medium truncate">
                        {n.actor_name}
                      </span>
                      <span className="block text-xs text-slate-600 line-clamp-2">
                        {n.text_preview}
                      </span>
                      <span className="block text-[11px] text-slate-400 mt-0.5">
                        {timeAgo(n.created_at)}
                      </span>
                    </span>
                    {!n.read && (
                      <span className="mt-2 shrink-0 w-2 h-2 rounded-full bg-[#8b3a2b]" aria-hidden />
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
